import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@connectrpc/connect-query'

import type { PhotoRef } from '@jotta/grpc-connect-openapi/photo'
import { Size_Preset } from '@jotta/grpc-connect-openapi/thumb'
import { getThumb } from '@jotta/grpc-connect-openapi/thumbQuery'
import { useFetchedImage } from '@jotta/photo-editor/hooks/useFetchedImage'

export interface PhotoLargeThumb {
  /** Object URL backed by the thumbnail bytes — safe to feed `<img>`. */
  url: string | undefined
  /** Decoded HTMLImageElement — ready for the editor canvas. */
  image: HTMLImageElement | undefined
  /** Raw thumbnail bytes — kept for EXIF/metadata passthrough on save. */
  bytes: Uint8Array | undefined
  /** True while the gRPC fetch or image decode is in flight. */
  isLoading: boolean
  /** Set when the thumbnail fetch failed. */
  error?: unknown
}

export interface PhotoLargeThumbOptions {
  /** Last-resort already-located thumbnail URL from the photo asset. */
  fallbackUrl?: string
}

/**
 * Fetch the LARGE thumbnail for a photo and expose it as a decoded
 * `HTMLImageElement` (for the editor canvas), an Object URL (for `<img>`
 * display), and raw bytes (for EXIF passthrough). React Query dedupes by
 * (RPC, args), so consumers sharing the same `ref` share a single gRPC
 * fetch.
 */
export function usePhotoLargeThumb(
  ref: PhotoRef | undefined,
  options?: PhotoLargeThumbOptions,
): PhotoLargeThumb {
  const { data, isLoading, error } = useQuery(
    getThumb,
    {
      ref: ref ? { case: 'photoRef', value: ref } : undefined,
      size: { size: { case: 'preset', value: Size_Preset.LARGE } },
    },
    {
      refetchOnMount: false,
      staleTime: 'static',
      enabled: Boolean(ref),
    },
  )

  const bytes = useMemo(() => {
    const response = data?.response
    return response?.case === 'found'
      ? new Uint8Array(response.value.data)
      : undefined
  }, [data])

  const responseError = useMemo(() => {
    const responseCase = data?.response?.case
    switch (responseCase) {
      case undefined:
      case 'found':
        return undefined
      case 'notFound':
        return new Error('Photo thumbnail not found')
      case 'notSupported':
        return new Error('Photo thumbnail not supported')
      default:
        return new Error(`Photo thumbnail unavailable: ${String(responseCase)}`)
    }
  }, [data])

  // Primary path: gRPC bytes → ObjectURL → Image decode.
  const [primary, setPrimary] = useState<{
    url: string | undefined
    image: HTMLImageElement | undefined
    isDecoding: boolean
  }>({ url: undefined, image: undefined, isDecoding: false })

  useEffect(() => {
    if (!bytes) {
      setPrimary({ url: undefined, image: undefined, isDecoding: false })
      return
    }
    const objectUrl = URL.createObjectURL(
      new Blob([bytes], { type: 'image/jpeg' }),
    )
    const img = new Image()
    img.crossOrigin = 'anonymous'
    let cancelled = false

    setPrimary({ url: objectUrl, image: undefined, isDecoding: true })

    img.addEventListener('load', () => {
      if (cancelled) {
        return
      }
      setPrimary({ url: objectUrl, image: img, isDecoding: false })
    })
    img.addEventListener('error', () => {
      if (cancelled) {
        return
      }
      setPrimary({ url: objectUrl, image: undefined, isDecoding: false })
    })
    img.src = objectUrl

    return () => {
      cancelled = true
      URL.revokeObjectURL(objectUrl)
    }
  }, [bytes])

  // Fallback path: fetch + decode via useFetchedImage.
  const fallbackEnabled = Boolean(options?.fallbackUrl) && !isLoading && !bytes
  const fallback = useFetchedImage(options?.fallbackUrl, {
    enabled: fallbackEnabled,
    mimeType: 'image/jpeg',
  })
  const fallbackIsStarting =
    fallbackEnabled && !fallback.image && !fallback.bytes && !fallback.error

  if (primary.url && bytes) {
    return {
      url: primary.url,
      image: primary.image,
      bytes,
      isLoading: primary.isDecoding,
      error: undefined,
    }
  }

  if (bytes) {
    return {
      url: undefined,
      image: undefined,
      bytes,
      isLoading: true,
      error: undefined,
    }
  }

  if (fallback.image && fallback.bytes) {
    return {
      url: undefined,
      image: fallback.image,
      bytes: fallback.bytes,
      isLoading: false,
      error: undefined,
    }
  }

  return {
    url: undefined,
    image: undefined,
    bytes: undefined,
    isLoading: isLoading || fallback.isLoading || fallbackIsStarting,
    error: fallbackIsStarting
      ? undefined
      : (error ?? responseError ?? fallback.error),
  }
}
